/**
 * Delivery-rule creation for the "Schedule" send option — POST
 * /api/v1/notifications/rules with trigger_type="scheduled". This is the
 * frontend half of the notifications scheduling work: the backend already
 * computes next_run_at on create/update (app/services/notifications
 * /delivery_rule_service.py) and, when a runner is wired back up, keeps
 * recomputing it after every fire.
 */
import { API_ENDPOINTS, apiService } from "../api";
import type {
  ScheduleConfig,
  Weekday,
} from "../../components/organisms/DeliverStory/types";
import type {
  DeliveryChannelDto,
  DeliveryContentConfigDto,
  DeliveryRuleDto,
  DeliveryScheduleDto,
  DeliveryScheduleEndsDto,
  ListDeliveryRulesParams,
  RuleStatusDto,
  TargetTypeDto,
} from "./deliveryRules.types";

export type {
  DeliveryChannelDto,
  DeliveryContentConfigDto,
  DeliveryFormatDto,
  DeliveryRuleDto,
  DeliveryScheduleDto,
  DeliveryScheduleEndsDto,
  DeliveryTargetDto,
  DeliveryTriggerConfigDto,
  ListDeliveryRulesParams,
  RuleStatusDto,
  TargetTypeDto,
  TriggerTypeDto,
} from "./deliveryRules.types";

const WEEKDAY_TO_ISO: Record<Weekday, number> = {
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sun: 7,
};

const ISO_TO_WEEKDAY = Object.fromEntries(
  Object.entries(WEEKDAY_TO_ISO).map(([day, iso]) => [iso, day]),
) as Record<number, Weekday>;

const UNIT_TO_FREQUENCY: Record<
  ScheduleConfig["unit"],
  DeliveryScheduleDto["frequency"]
> = {
  day: "daily",
  week: "weekly",
  month: "monthly",
};

const FREQUENCY_TO_UNIT: Record<
  DeliveryScheduleDto["frequency"],
  ScheduleConfig["unit"]
> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
};

/** Day-of-month from an ISO yyyy-mm-dd string — same derivation
 * utils/scheduleTypes.ts's dayOfMonthFromIso uses: monthly recurrence has
 * no separate day-of-month field in ScheduleConfig, it's implied by
 * whatever day of the month "Starts on" falls on. */
const dayOfMonthFromIso = (iso: string): number | undefined => {
  const parts = iso.trim().split("-");
  if (parts.length !== 3) return undefined;
  const dom = Number(parts[2]);
  return Number.isFinite(dom) && dom >= 1 && dom <= 31 ? dom : undefined;
};

/** `endsMode` is only ever "never"/"on" by the time this runs — DeliverStory's
 * `ScheduleEndsField` never lets the user select "after" — so anything else
 * (including a stale "after") maps to "never" rather than silently sending
 * an end date-less "on". */
const toEndsDto = (schedule: ScheduleConfig): DeliveryScheduleEndsDto =>
  schedule.endsMode === "on" && schedule.endsOn
    ? { kind: "on", date: schedule.endsOn }
    : { kind: "never" };

export function mapScheduleConfigToDeliverySchedule(
  schedule: ScheduleConfig,
): DeliveryScheduleDto {
  const hour = schedule.refreshHour ?? 0;
  return {
    frequency: UNIT_TO_FREQUENCY[schedule.unit],
    time_of_day: `${String(hour).padStart(2, "0")}:00:00`,
    timezone: schedule.timezone || "UTC",
    every: schedule.every,
    weekdays:
      schedule.unit === "week"
        ? schedule.repeatOn.map((d) => WEEKDAY_TO_ISO[d])
        : [],
    day_of_month:
      schedule.unit === "month"
        ? dayOfMonthFromIso(schedule.startsOn)
        : undefined,
    starts_on: schedule.startsOn || undefined,
    ends: toEndsDto(schedule),
  };
}

/** Inverse of `mapScheduleConfigToDeliverySchedule` — used to prefill the
 * Schedule tab from a story's already-existing delivery rule (single
 * schedule per story: see `getScheduledDeliveryRuleForStory`). `fallback`
 * supplies fields the wire shape doesn't carry (`endsAfter` — always
 * unsupported here, see `DeliveryScheduleEndsDto`) or that a malformed
 * `time_of_day` can't be parsed into.
 *
 * `dto.ends` itself is read defensively: `GET /rules` returns each rule's
 * `trigger_config` exactly as stored, with no re-validation against the
 * current `DeliverySchedule` schema — a rule created before the `ends`
 * field existed has no `ends` key in its stored JSON at all, even though
 * the backend schema defaults it to `{kind: "never"}` on write. */
export function mapDeliveryScheduleToScheduleConfig(
  dto: DeliveryScheduleDto,
  fallback: ScheduleConfig,
): ScheduleConfig {
  const hour = Number(dto.time_of_day.split(":")[0]);
  const endsKind = dto.ends?.kind === "on" ? "on" : "never";
  return {
    ...fallback,
    every: dto.every,
    unit: FREQUENCY_TO_UNIT[dto.frequency],
    repeatOn: dto.weekdays
      .map((iso) => ISO_TO_WEEKDAY[iso])
      .filter((day): day is Weekday => Boolean(day)),
    refreshHour: Number.isFinite(hour) ? hour : fallback.refreshHour,
    timezone: dto.timezone || fallback.timezone,
    startsOn: dto.starts_on ?? fallback.startsOn,
    endsMode: endsKind,
    endsOn: endsKind === "on" ? (dto.ends as { date: string }).date : "",
  };
}

/** One source of truth for "which channel implies which target_type" — the
 * only two schedulable channels the backend accepts today
 * (`DeliveryChannelDto`). Teams has no backend support yet (no
 * `DeliveryChannelDto`/`TargetTypeDto` entry for it), so callers must not
 * reach this for platform "teams" — surface "not supported yet" before
 * calling instead of guessing a target_type here. */
const CHANNEL_TARGET_TYPE: Record<DeliveryChannelDto, TargetTypeDto> = {
  slack: "slack_channel",
  email: "email_address",
};

/**
 * The rule's `content_config` plus its top-level `format`. `format` must be
 * sent: the backend defaults it to PDF and mirrors it over
 * `content_config.attachment_format`, so leaving it out silently turned
 * PPTX deliveries into PDFs.
 */
const deliveryContent = (params: {
  message: string;
  storyId: string;
  workspaceId: string;
  attachmentFormat: "pdf" | "pptx";
  artifact?: DeliveryContentConfigDto["artifact"];
  theme?: string;
}) => ({
  format: params.attachmentFormat,
  content_config: {
    text: params.message,
    story_id: params.storyId,
    workspace_id: params.workspaceId,
    attachment_format: params.attachmentFormat,
    ...(params.artifact ? { artifact: params.artifact } : {}),
    ...(params.theme ? { theme: params.theme } : {}),
  },
});

export interface CreateScheduledDeliveryRuleParams {
  name: string;
  /** "slack" | "email" — picks both the rule's `channel` and every target's
   * `target_type` (see `CHANNEL_TARGET_TYPE`). */
  channel: DeliveryChannelDto;
  channelIds: string[];
  message: string;
  storyId: string;
  workspaceId: string;
  attachmentFormat: "pdf" | "pptx";
  /** Saved so Edit restores the Artifact choice. */
  artifact?: DeliveryContentConfigDto["artifact"];
  /** Presentation theme for a rendered deck; omit → the default theme. */
  theme?: string;
  schedule: ScheduleConfig;
}

export interface DeliveryRuleResponse {
  id: string;
  next_run_at: string | null;
}

export async function createScheduledDeliveryRule(
  params: CreateScheduledDeliveryRuleParams,
): Promise<DeliveryRuleResponse> {
  // Mirrors sendToSlackChannels's same guard (storyExportApi.ts) — the
  // backend happily creates a delivery_rule with zero targets (nothing
  // to fan out to at fire time isn't a validation error server-side),
  // so nothing stops a targetless schedule from being created unless
  // this is checked here.
  if (params.channelIds.length === 0) {
    throw new Error("Select at least one destination before scheduling.");
  }

  return apiService.post<DeliveryRuleResponse>(
    API_ENDPOINTS.notifications.rules(),
    {
      name: params.name,
      // Legacy int column, unrelated to the real story UUID — the actual
      // story reference lives in content_config.story_id below (see
      // dispatch_service.py's _resolve_slack_attachment for why).
      story_id: 1,
      channel: params.channel,
      trigger_type: "scheduled",
      trigger_config: {
        schedule: mapScheduleConfigToDeliverySchedule(params.schedule),
      },
      ...deliveryContent(params),
      targets: params.channelIds.map((channelId) => ({
        target_type: CHANNEL_TARGET_TYPE[params.channel],
        target_ref: channelId,
      })),
    },
  );
}

export interface CreateAlertDeliveryRuleParams {
  name: string;
  channel: DeliveryChannelDto;
  channelIds: string[];
  message: string;
  storyId: string;
  workspaceId: string;
  attachmentFormat: "pdf" | "pptx";
  /** Saved so Edit restores the Artifact choice. */
  artifact?: DeliveryContentConfigDto["artifact"];
  /** Presentation theme the alert's attachment is rendered in; omit → default. */
  theme?: string;
  /** The story_alerts row this rule delivers for — see
   * `DeliveryRule.alert_id`'s docstring (magic-core). One alert may own
   * several rules, one per channel. */
  alertId: string;
}

/**
 * Delivery-rule creation for the "Alert on condition" send option —
 * same POST /notifications/rules endpoint as `createScheduledDeliveryRule`,
 * but `alert_id` instead of `trigger_config.schedule`: alerts fire on
 * story refresh, not their own cadence (see
 * `app/services/notifications/delivery_rule_service.py`'s
 * `_compute_next_run_at` docstring), so `trigger_config` stays empty here.
 */
export async function createAlertDeliveryRule(
  params: CreateAlertDeliveryRuleParams,
): Promise<DeliveryRuleResponse> {
  if (params.channelIds.length === 0) {
    throw new Error("Select at least one destination before creating the alert.");
  }

  return apiService.post<DeliveryRuleResponse>(
    API_ENDPOINTS.notifications.rules(),
    {
      name: params.name,
      story_id: 1,
      channel: params.channel,
      trigger_type: "alert",
      trigger_config: {},
      alert_id: params.alertId,
      ...deliveryContent(params),
      targets: params.channelIds.map((channelId) => ({
        target_type: CHANNEL_TARGET_TYPE[params.channel],
        target_ref: channelId,
      })),
    },
  );
}

/**
 * `GET /notifications/rules` — list a tenant's delivery rules, optionally
 * scoped to one story. Backend caps `limit` at 500 and returns a bare
 * array (no total count), hence `ListDeliveryRulesParams` defaulting to a
 * high limit and the caller doing client-side pagination.
 */
export async function listDeliveryRules(
  params: ListDeliveryRulesParams = {},
): Promise<DeliveryRuleDto[]> {
  return apiService.get<DeliveryRuleDto[]>(
    API_ENDPOINTS.notifications.rules(),
    params,
  );
}

/**
 * A story has at most one scheduled delivery rule — this is what lets the
 * Schedule tab tell "create" from "update" apart, and what it prefills
 * from. `story_uuid`/`trigger_type` are backend query params added
 * specifically for this lookup (the rule's own `story_id` column is a
 * legacy int unrelated to the real story UUID — see
 * `mapScheduleConfigToDeliverySchedule`'s caller for why the real UUID
 * lives in `content_config.story_id` instead).
 */
export async function getScheduledDeliveryRuleForStory(
  storyId: string,
): Promise<DeliveryRuleDto | null> {
  if (!storyId) return null;
  const rules = await apiService.get<DeliveryRuleDto[]>(
    API_ENDPOINTS.notifications.rules(),
    { story_uuid: storyId, trigger_type: "scheduled", limit: 1 },
  );
  return rules[0] ?? null;
}

/**
 * `GET /notifications/rules/{id}` — read one rule in full, used to prefill
 * the Edit form from a specific row in the deliveries table (as opposed to
 * `getScheduledDeliveryRuleForStory`, which only ever finds "the story's
 * one schedule"). Throws (via `apiService`) on 404/403 same as any other
 * call — the caller surfaces that through `getApiErrorMessage`.
 */
export async function getDeliveryRuleById(
  ruleId: string,
): Promise<DeliveryRuleDto> {
  return apiService.get<DeliveryRuleDto>(
    API_ENDPOINTS.notifications.ruleById(ruleId),
  );
}

export interface UpdateDeliveryRuleParams {
  ruleId: string;
  name: string;
  /** "slack" | "email" — see `CHANNEL_TARGET_TYPE`. Rule's own top-level
   * `channel` column isn't patchable (`DeliveryRuleUpdate` doesn't accept
   * it — a rule's channel is fixed at creation), only `targets[].target_type`
   * is re-derived from it here. */
  channel: DeliveryChannelDto;
  channelIds: string[];
  message: string;
  storyId: string;
  workspaceId: string;
  attachmentFormat: "pdf" | "pptx";
  /** Saved so Edit restores the Artifact choice. */
  artifact?: DeliveryContentConfigDto["artifact"];
  /** Presentation theme for a rendered deck; omit → the default theme. */
  theme?: string;
  /** Omit for a rule that isn't a schedule (immediate/alert) — leaves
   * `trigger_config` untouched server-side rather than forcing a schedule
   * onto a rule that was never one. */
  schedule?: ScheduleConfig;
}

/** Updates an existing rule in place — same wire shape as
 * `createScheduledDeliveryRule` minus the create-only `story_id`/`channel`
 * fields `DeliveryRuleUpdate` doesn't accept. Always replaces the full
 * recipient list (not a diff) so removing a channel in the UI actually
 * removes it server-side too. Used both by the single-schedule-per-story
 * auto-update path and by the per-row Edit action. */
export async function updateDeliveryRule(
  params: UpdateDeliveryRuleParams,
): Promise<DeliveryRuleResponse> {
  if (params.channelIds.length === 0) {
    throw new Error("Select at least one destination before scheduling.");
  }

  return apiService.patch<DeliveryRuleResponse>(
    API_ENDPOINTS.notifications.ruleById(params.ruleId),
    {
      name: params.name,
      ...(params.schedule
        ? {
            trigger_config: {
              schedule: mapScheduleConfigToDeliverySchedule(params.schedule),
            },
          }
        : {}),
      ...deliveryContent(params),
      targets: params.channelIds.map((channelId) => ({
        target_type: CHANNEL_TARGET_TYPE[params.channel],
        target_ref: channelId,
      })),
    },
  );
}

/**
 * `PATCH /notifications/rules/{id}` with only `status` set — the pause /
 * resume / cancel lever (`DELIVERY_FLOW_CRUD_PLAN.md` §1.4). The backend's
 * status transition table (active⇄paused, either→cancelled) rejects an
 * illegal transition with a 409 whose message is surfaced verbatim by the
 * caller via `getApiErrorMessage` — this function does not re-validate the
 * transition client-side, since the row menu already only offers the
 * actions that are legal for the row's current status.
 */
export async function setDeliveryRuleStatus(
  ruleId: string,
  status: RuleStatusDto,
): Promise<DeliveryRuleDto> {
  return apiService.patch<DeliveryRuleDto>(
    API_ENDPOINTS.notifications.ruleById(ruleId),
    { status },
  );
}

/**
 * `DELETE /notifications/rules/{id}` — permanently removes the rule (and
 * cascades its dispatch history server-side). `204 No Content`: nothing to
 * parse, nothing to return. Story-owner-only server-side; a non-owner gets
 * a 403 whose message the caller surfaces via `getApiErrorMessage`.
 */
export async function deleteDeliveryRule(ruleId: string): Promise<void> {
  await apiService.delete(API_ENDPOINTS.notifications.ruleById(ruleId));
}
